import React, { useState, useEffect } from 'react';
import { IcPlus, IcPencil, IcTrash, IcClose, IcSave, IcAward } from '../../theme/icons';
import clientCategoryService from '../../services/clientCategoryService';
import Swal from '../../theme/swal';
import SectionCard from '../../components/SectionCard';
import { CardSkeleton } from '../../components/Skeleton';

// `embedded` : rendu dans Paramètres › Catégories clients (entête compact, sans chip).
const ClientCategoryManagement = ({ embedded = false } = {}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    points_value: '',
    required_score: '',
    remise: '',
    features: []
  });
  const [featureInput, setFeatureInput] = useState('');
  const [featureScore, setFeatureScore] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await clientCategoryService.getAllCategories();
      // Handle paginated response
      const categoriesArray = data.results ? data.results : (Array.isArray(data) ? data : []);
      setCategories(categoriesArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
      Swal.fire('Erreur', 'Impossible de charger les catégories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        points_value: category.points_value,
        required_score: category.required_score,
        remise: category.remise || '',
        features: category.features || []
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        points_value: '',
        required_score: '',
        remise: '',
        features: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      points_value: '',
      required_score: '',
      remise: '',
      features: []
    });
    setFeatureInput('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, {
          points: parseInt(featureScore) || 0,
          feature: featureInput.trim()
        }]
      }));
      setFeatureInput('');
      setFeatureScore('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      Swal.fire('Erreur', 'Le nom est obligatoire', 'error');
      return;
    }

    try {
      const categoryData = {
        name: formData.name,
        points_value: parseFloat(formData.points_value) || 0,
        required_score: parseInt(formData.required_score) || 0,
        remise: parseInt(formData.remise) || 0,
        features: formData.features
      };

      if (editingCategory) {
        await clientCategoryService.updateCategory(editingCategory.id, categoryData);
        Swal.fire('Succès', 'Catégorie mise à jour avec succès', 'success');
      } else {
        await clientCategoryService.createCategory(categoryData);
        Swal.fire('Succès', 'Catégorie créée avec succès', 'success');
      }

      handleCloseModal();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      Swal.fire('Erreur', 'Impossible de sauvegarder la catégorie', 'error');
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await clientCategoryService.deleteCategory(categoryToDelete.id);
      Swal.fire('Supprimé!', 'La catégorie a été supprimée.', 'success');
      fetchCategories();
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      Swal.fire('Erreur', 'Impossible de supprimer la catégorie', 'error');
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
  const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';
  const featureText = (f) => (typeof f === 'string' ? f : f.feature);
  const featurePts = (f) => (typeof f === 'object' && f.points !== undefined ? `${f.points} pts` : '0 pts');

  const listContent = loading ? (
        <div className="flex justify-center items-center h-64">
          <CardSkeleton />
        </div>
      ) : categories.length === 0 ? (
        <div className="empty-ki rounded-2xl p-12">
          <IcAward size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">Aucune catégorie client</p>
          <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">Créez vos niveaux de fidélité : points, remise, score requis et avantages.</p>
          <button type="button" onClick={() => handleOpenModal()} className="btn-add-block w-auto mt-4 px-4 py-2.5">
            <IcPlus className="mr-2" /> Ajouter une catégorie
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div key={category.id} className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col">
              {/* Haut : nom 600 + nb clients ; Modifier / Supprimer (règles icônes) */}
              <div className="flex items-start justify-between gap-2 mb-4">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="h-10 w-10 shrink-0 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center"><IcAward size={18} /></span>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 leading-tight truncate">{category.name}</p>
                    <p className="text-xs text-gray-400 tabular-nums mt-0.5">{category.customer_count || 0} client(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => handleOpenModal(category)} className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors" title="Modifier" aria-label="Modifier la catégorie">
                    <IcPencil />
                  </button>
                  <button type="button" onClick={() => handleDeleteClick(category)} className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Supprimer" aria-label="Supprimer la catégorie">
                    <IcTrash />
                  </button>
                </div>
              </div>

              {/* 3 tuiles (même motif que la carte client) : Points / Remise / Score requis */}
              <div className="flex gap-2 mb-4">
                <div className="flex-1 border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">Points</p>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">{category.points_value} %</p>
                </div>
                <div className="flex-1 border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">Remise</p>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">{category.remise || 0} %</p>
                </div>
                <div className="flex-1 border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400 whitespace-nowrap">Score</p>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">{category.required_score} pts</p>
                </div>
              </div>

              {/* Avantages : liste filetée, coût en points = bdg-pts (vert plein, kit) */}
              <p className="text-[11px] tracking-[0.8px] uppercase text-gray-400 mb-1">Avantages</p>
              {category.features && category.features.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {category.features.map((feature, index) => (
                    <li key={index} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-sm text-gray-700 min-w-0 truncate">{featureText(feature)}</span>
                      <span className="bdg bdg-pts shrink-0">{featurePts(feature)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400 py-2">Aucun avantage.</p>
              )}
            </div>
          ))}
        </div>
      );

  return (
    <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
      {/* En-tête : chip gris (page seule) ou titre de section (Paramètres),
          « Ajouter une catégorie » btn-add-block à droite. */}
      {/* Paramètres (Studio owner 2026-09-09, même règle que Services) : pas de
          titre — les cartes seules, « Ajouter une catégorie » dessous à droite. */}
      {!embedded && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm self-start">
          <IcAward size={13} /> Gestion des catégories client
        </button>
        <button type="button" onClick={() => handleOpenModal()} className="btn-add-block w-auto px-4 py-2.5 transition-colors self-start sm:self-auto">
          <IcPlus className="mr-2" />
          Ajouter une catégorie
        </button>
      </div>
      )}

      {/* Paramètres : même cadre que Tarification (owner 2026-09-09). */}
      {embedded ? (
        <SectionCard
          title="Catégories clients"
          sub={`${categories.length} catégorie(s) de fidélité`}
          action={!loading && (
            <button
              type="button"
              onClick={() => handleOpenModal()}
              className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
              title="Ajouter une catégorie"
              aria-label="Ajouter une catégorie"
            >
              <IcPlus />
            </button>
          )}
        >
          {listContent}
        </SectionCard>
      ) : listContent}

      {/* Modale Nouvelle / Modifier la catégorie (Brand Kit §05) */}
      {showModal && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h2 className="modal-title text-lg">
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h2>
              <button type="button" onClick={handleCloseModal} className="text-gray-400 hover:text-white transition-colors" aria-label="Fermer">
                <IcClose size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                <div>
                  <label className={labelClass} htmlFor="cc-name">Nom de la catégorie <span className="req">*</span></label>
                  <input
                    id="cc-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Ex : Fidèle, Fidèle Gold…"
                    autoFocus
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass} htmlFor="cc-points">Points (%)</label>
                    <input id="cc-points" type="number" name="points_value" value={formData.points_value} onChange={handleInputChange} className={inputClass} placeholder="0" min="0" step="0.1" />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="cc-remise">Remise (%)</label>
                    <input id="cc-remise" type="number" name="remise" value={formData.remise} onChange={handleInputChange} className={inputClass} placeholder="0" min="0" max="100" step="1" />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="cc-score">Score requis</label>
                    <input id="cc-score" type="number" name="required_score" value={formData.required_score} onChange={handleInputChange} className={inputClass} placeholder="0" min="0" step="1" />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Avantages</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="number"
                      value={featureScore}
                      onChange={(e) => setFeatureScore(e.target.value)}
                      className="inp w-28 shrink-0 px-3.5 py-2.5 rounded-xl text-base text-gray-900"
                      placeholder="Points"
                      aria-label="Points de l'avantage"
                      min="0"
                    />
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                      className="inp flex-1 min-w-0 px-3.5 py-2.5 rounded-xl text-base text-gray-900"
                      placeholder="Description de l'avantage…"
                      aria-label="Description de l'avantage"
                    />
                    <button type="button" onClick={handleAddFeature} className="btn-add-pop h-[42px] w-[42px] shrink-0" title="Ajouter l'avantage" aria-label="Ajouter l'avantage">
                      <IcPlus />
                    </button>
                  </div>
                  {formData.features.length > 0 ? (
                    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                      {formData.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-3 px-3 py-2">
                          <span className="bdg bdg-pts shrink-0 min-w-[3.5rem] justify-center">{featurePts(feature)}</span>
                          <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">{featureText(feature)}</span>
                          <button type="button" onClick={() => handleRemoveFeature(index)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Retirer l'avantage">
                            <IcClose size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400">Aucun avantage pour le moment.</p>
                  )}
                </div>
              </div>

              {/* Pied de modale (Brand Kit §05) : filet gray-100, Annuler ghost + encre */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3 mt-6">
                <button type="button" onClick={handleCloseModal} className="btn-g px-4 py-2.5 transition-colors">
                  Annuler
                </button>
                <button type="submit" className="btn-ink px-4 py-2.5 transition-colors">
                  <IcSave className="mr-2" />
                  {editingCategory ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation de suppression (même patron de modale, action btn-db — kit « Destructif — bordé », owner 2026-09-10 : tous les Supprimer) */}
      {showDeleteDialog && (
        <div className="modal-backdrop z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) handleCancelDelete(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">Supprimer la catégorie</h3>
              <button type="button" onClick={handleCancelDelete} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose size={18} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700">
                Supprimer la catégorie <span className="font-semibold text-gray-900">« {categoryToDelete?.name} »</span> ?
              </p>
              <p className="text-xs text-red-600 mt-2">Cette action est irréversible.</p>
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3 mt-5">
                <button type="button" onClick={handleCancelDelete} className="btn-g px-4 py-2.5 transition-colors">Annuler</button>
                <button type="button" onClick={handleConfirmDelete} className="btn-db px-4 py-2.5 transition-colors"><IcTrash /> Supprimer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCategoryManagement;
